import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

// Type manuel en attendant la regeneration des types Supabase
interface WahaSession {
  id: string;
  team_id: string;
  session_name: string;
  phone: string | null;
  status: string;
  pairing_code: string | null;
  connected_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Recupere la session WAHA du team courant. */
export function useWahaSession(polling = false) {
  return useQuery({
    queryKey: ["waha-session"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("waha_sessions" as string)
        .select("*")
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return (data as unknown as WahaSession) ?? null;
    },
    refetchInterval: polling ? 5000 : false,
  });
}

/** Cree une session WAHA et demande un pairing code. */
export function useCreateWahaSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (phone: string) => {
      // Etape 1 : Creer la session
      const { data: createData, error: createError } = await supabase.functions.invoke(
        "waha-session-manager",
        { body: { action: "create" } },
      );

      if (createError) throw new Error(createError.message);
      if (createData?.error) throw new Error(createData.error);

      // Etape 2 : Demander le pairing code
      const { data: codeData, error: codeError } = await supabase.functions.invoke(
        "waha-session-manager",
        { body: { action: "request-code", phone } },
      );

      if (codeError) throw new Error(codeError.message);
      if (codeData?.error) throw new Error(codeData.error);

      return codeData as { success: boolean; pairing_code: string; phone: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waha-session"] });
    },
    onError: (error) => {
      // Traduire les messages techniques en messages user-friendly
      let description = error.message;
      if (description.includes("non-2xx") || description.includes("FunctionsHttpError")) {
        description = "Le service WhatsApp est temporairement indisponible. Réessayez dans quelques instants.";
      } else if (description.includes("not configured") || description.includes("injoignable")) {
        description = "Le service WhatsApp n'est pas configuré. Contactez le support.";
      }
      toast({
        title: "Erreur WhatsApp",
        description,
        variant: "destructive",
      });
    },
  });
}

/** Supprime la session WAHA. */
export function useDeleteWahaSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        "waha-session-manager",
        { body: { action: "delete" } },
      );

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["waha-session"] });
      toast({ title: "WhatsApp déconnecté" });
    },
    onError: (error) => {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Rafraichit le statut de la session depuis WAHA. */
export function useRefreshWahaStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        "waha-session-manager",
        { body: { action: "status" } },
      );

      // Ignorer silencieusement les 401 (JWT expire) — la session
      // sera rafraichie au prochain page load. Evite le spam console.
      if (error) {
        if (error.message?.includes("non-2xx") || error.message?.includes("401")) {
          console.debug("[useRefreshWahaStatus] Auth expired, skipping refresh");
          return null;
        }
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ["waha-session"] });
      }
    },
  });
}
